import type { JSX } from 'react';
import { useStore } from '@/state/store';
import { Panel, Field, Row } from '@/components/ui/Panel';

export function SolverPanel(): JSX.Element {
  const sim = useStore((s) => s.sim);
  const setSim = useStore((s) => s.setSim);
  const frame = useStore((s) => s.frame);

  return (
    <Panel title="Solver" defaultOpen={false}>
      <div className="seg" style={{ marginBottom: 8 }}>
        <button
          aria-pressed={sim.mode === 'compliant'}
          onClick={() => setSim({ mode: 'compliant' })}
          title="Bar-and-hinge model integrated with XPBD. Works on any pattern; facets may bend and stretch."
        >
          Compliant
        </button>
        <button
          aria-pressed={sim.mode === 'rigid'}
          onClick={() => setSim({ mode: 'rigid' })}
          title="Exactly rigid facets with loop-closure constraints. Only converges for rigid-foldable patterns."
        >
          Rigid
        </button>
      </div>

      {sim.mode === 'compliant' ? (
        <>
          <Field
            label="Axial compliance"
            title="Smaller is stiffer. Paper stretches very little, so this should stay tiny."
          >
            <input
              type="number"
              step="1e-10"
              min="0"
              value={sim.compliant.axialCompliance}
              onChange={(e) =>
                setSim({
                  compliant: { ...sim.compliant, axialCompliance: Number(e.target.value) },
                })
              }
            />
          </Field>
          <Field label="Facet compliance" title="How readily a facet bends along its triangulation diagonal.">
            <input
              type="number"
              step="1e-7"
              min="0"
              value={sim.compliant.facetCompliance}
              onChange={(e) =>
                setSim({ compliant: { ...sim.compliant, facetCompliance: Number(e.target.value) } })
              }
            />
          </Field>
          <Field label="Crease compliance" title="How readily a crease deviates from its target angle.">
            <input
              type="number"
              step="1e-6"
              min="0"
              value={sim.compliant.creaseCompliance}
              onChange={(e) =>
                setSim({ compliant: { ...sim.compliant, creaseCompliance: Number(e.target.value) } })
              }
            />
          </Field>
          <Field label="Damping">
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={sim.compliant.damping}
              onChange={(e) =>
                setSim({ compliant: { ...sim.compliant, damping: Number(e.target.value) } })
              }
            />
          </Field>
          <Field label="Substeps">
            <input
              type="number"
              step="1"
              min="1"
              max="40"
              value={sim.compliant.substeps}
              onChange={(e) =>
                setSim({ compliant: { ...sim.compliant, substeps: Number(e.target.value) } })
              }
            />
          </Field>
          <Field label="Iterations / substep">
            <input
              type="number"
              step="1"
              min="1"
              max="20"
              value={sim.compliant.iterations}
              onChange={(e) =>
                setSim({ compliant: { ...sim.compliant, iterations: Number(e.target.value) } })
              }
            />
          </Field>
          <p className="note">
            XPBD compliance is a physical parameter divided by Δt², so changing the substep count
            changes accuracy, not stiffness.
          </p>
        </>
      ) : (
        <>
          <Field label="Damping (λ)" title="Levenberg-Marquardt ridge on the normal equations.">
            <input
              type="number"
              step="1e-7"
              min="0"
              value={sim.rigid.damping}
              onChange={(e) => setSim({ rigid: { ...sim.rigid, damping: Number(e.target.value) } })}
            />
          </Field>
          <Field label="Max iterations">
            <input
              type="number"
              step="1"
              min="1"
              max="500"
              value={sim.rigid.maxIterations}
              onChange={(e) =>
                setSim({ rigid: { ...sim.rigid, maxIterations: Number(e.target.value) } })
              }
            />
          </Field>
          <Field label="Step scale" title="Fraction of the remaining distance to the target taken per solve.">
            <input
              type="number"
              step="0.05"
              min="0.01"
              max="1"
              value={sim.rigid.stepScale}
              onChange={(e) => setSim({ rigid: { ...sim.rigid, stepScale: Number(e.target.value) } })}
            />
          </Field>
          <Field label="Tolerance">
            <input
              type="number"
              step="1e-9"
              min="0"
              value={sim.rigid.tolerance}
              onChange={(e) =>
                setSim({ rigid: { ...sim.rigid, tolerance: Number(e.target.value) } })
              }
            />
          </Field>
          <p className="note">
            The rigid solver keeps every facet exactly rigid. If a pattern is not rigid-foldable it
            reports a loop-closure residual instead of bending the paper to make it fit.
          </p>
        </>
      )}

      <Field label="Collision detection" title="Broad-phase BVH plus triangle–triangle tests. Detection only: contact is not resolved.">
        <input
          type="checkbox"
          checked={sim.collisionEnabled}
          onChange={(e) => setSim({ collisionEnabled: e.target.checked })}
        />
      </Field>

      {frame ? (
        <>
          <Row label="Mesh triangles" value={frame.meshStats.triangles} />
          <Row label="Hinges" value={frame.meshStats.hinges} />
          <Row label="Bars" value={frame.meshStats.bars} />
          <Row label="Pieces" value={frame.meshStats.pieces} />
        </>
      ) : null}
    </Panel>
  );
}
